import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import type { ProfileAttendedEvent } from "@satyrsmc/shared/dto/member/profile";
import { Card, CardContent } from "@/components/ui/card";
import { formatDate } from "@/lib/date-utils";
import { TEXT_LINK_CLASS } from "@/lib/linkStyles";
import { trpc } from "@/trpc";

/** "May 1, 2026", or "May 1, 2026 – May 3, 2026" for a multi-day event. */
function eventDates(event: ProfileAttendedEvent): string | null {
  if (!event.start_date) return null;
  if (!event.end_date || event.end_date === event.start_date) return formatDate(event.start_date);
  return `${formatDate(event.start_date)} – ${formatDate(event.end_date)}`;
}

/**
 * A profile's Events tab: past events the user RSVP'd yes to and didn't
 * cancel, most recent first, each linking to its event page.
 */
export function ProfileEventsTab({ username }: { username: string }) {
  const { data, isLoading, error } = trpc.members.profile.attendedEvents.useQuery({ username });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          {error.message}
        </CardContent>
      </Card>
    );
  }

  const events = data ?? [];
  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          No attended events yet.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <ul className="divide-y">
          {events.map((event) => (
            <li
              key={event.id}
              className="flex items-center justify-between gap-3 py-3 text-sm first:pt-0 last:pb-0"
            >
              <Link to={`/events/${event.id}`} className={TEXT_LINK_CLASS}>
                {event.name}
              </Link>
              <span className="shrink-0 text-xs text-muted-foreground">{eventDates(event)}</span>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
